import React, { useState, useRef, useCallback } from "react";
import { useSpring, animated, to } from "react-spring";

interface SwipableButtonProps {
  callbackFunction?: () => void;
  disabled: boolean;
}

const THUMB_SIZE = 48;
const TRACK_HEIGHT = 56;
const TRACK_PADDING = 4;
const MAX_TRAVEL_OFFSET = THUMB_SIZE + TRACK_PADDING; // px from right edge

const SwipableButton: React.FC<SwipableButtonProps> = ({ callbackFunction, disabled }) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isSwiped, setIsSwiped] = useState(false);
  const [{ x }, api] = useSpring(() => ({ x: 0, config: { tension: 260, friction: 24 } }));
  const thumbRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);

  const getMaxTravel = useCallback(() => {
    if (!trackRef.current) return 200;
    return trackRef.current.clientWidth - MAX_TRAVEL_OFFSET;
  }, []);

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled || isSwiped) return;
    setIsPressed(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    startXRef.current = clientX;
  };

  const handlePointerMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isPressed || !trackRef.current) return;
      const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
      let dx = clientX - startXRef.current;
      if (dx < 0) dx = 0;
      const max = getMaxTravel();
      if (dx > max) dx = max;
      api.start({ x: dx, immediate: true });
    },
    [isPressed, api, getMaxTravel],
  );

  const handlePointerUp = useCallback(() => {
    if (!isPressed) return;
    setIsPressed(false);
    const max = getMaxTravel();
    if (x.get() >= max * 0.85) {
      setIsSwiped(true);
      api.start({ x: max });
      callbackFunction?.();
      // Reset after a short delay so the component is reusable
      setTimeout(() => {
        setIsSwiped(false);
        api.start({ x: 0 });
      }, 600);
    } else {
      api.start({ x: 0 });
    }
  }, [isPressed, x, api, getMaxTravel, callbackFunction]);

  React.useEffect(() => {
    if (isPressed) {
      window.addEventListener("mousemove", handlePointerMove);
      window.addEventListener("mouseup", handlePointerUp);
      window.addEventListener("touchmove", handlePointerMove, { passive: false });
      window.addEventListener("touchend", handlePointerUp);
    }
    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
    };
  }, [isPressed, handlePointerMove, handlePointerUp]);

  // Derived animated values
  const progress = x.to([0, getMaxTravel() || 200], [0, 1]);
  const labelOpacity = progress.to((p: number) => 1 - p * 1.8); // fade out quickly
  const fillWidth = x.to((v: number) => v + THUMB_SIZE + TRACK_PADDING);

  return (
    <div
      ref={trackRef}
      style={{
        position: "relative",
        height: TRACK_HEIGHT,
        borderRadius: TRACK_HEIGHT / 2,
        background: disabled ? "#e0e0e0" : "#e8f4fd",
        border: disabled ? "1px solid #ccc" : "1px solid #b3d9f2",
        margin: "16px",
        overflow: "hidden",
        cursor: disabled ? "not-allowed" : "default",
        opacity: disabled ? 0.55 : 1,
        transition: "opacity 0.2s, background 0.2s",
        touchAction: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      {/* Filled progress background */}
      <animated.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: fillWidth,
          borderRadius: TRACK_HEIGHT / 2,
          background: isSwiped
            ? "linear-gradient(90deg, #367c2b, #4a9e3a)"
            : "linear-gradient(90deg, rgba(6,138,252,0.15), rgba(6,138,252,0.30))",
          transition: isSwiped ? "background 0.3s" : undefined,
          pointerEvents: "none",
        }}
      />

      {/* Label text */}
      <animated.div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.875rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          color: disabled ? "#999" : "#333",
          opacity: labelOpacity,
          pointerEvents: "none",
        }}
      >
        {isSwiped ? "Starting..." : "Swipe to Start Autonomy"}
      </animated.div>

      {/* Chevron hints (static, behind thumb) */}
      {!isSwiped && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: THUMB_SIZE + TRACK_PADDING + 12,
            transform: "translateY(-50%)",
            display: "flex",
            gap: 2,
            opacity: 0.25,
            pointerEvents: "none",
          }}
        >
          {[0, 1, 2].map((i) => (
            <svg key={i} width="8" height="14" viewBox="0 0 8 14" fill="none">
              <path
                d="M1 1l5 6-5 6"
                stroke="#068afc"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ))}
        </div>
      )}

      {/* Draggable thumb */}
      <animated.div
        ref={thumbRef}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        style={{
          position: "absolute",
          top: TRACK_PADDING,
          left: TRACK_PADDING,
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          borderRadius: THUMB_SIZE / 2,
          background: disabled
            ? "#bbb"
            : isSwiped
              ? "#367c2b"
              : "linear-gradient(135deg, #068afc, #0570d4)",
          boxShadow: isPressed
            ? "0 2px 8px rgba(6,138,252,0.4)"
            : "0 1px 4px rgba(0,0,0,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "grab",
          touchAction: "none",
          userSelect: "none",
          transform: to([x], (xv) => `translateX(${xv}px)`),
          transition: isPressed ? undefined : "box-shadow 0.2s",
        }}
      >
        {isSwiped ? (
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path
              d="M5 11.5l4 4L17 7"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path
              d="M8 5l7 6-7 6"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </animated.div>
    </div>
  );
};

export default SwipableButton;
